import 'hammerjs';
import React from 'react';
//import { AnnotationPopover } from '../../components/Annotations/AnnotationPopover'; -- NEED TO IMPLEMENT THIS STILL

import {
  Annotation,
  PDFTextHighlightAnnotation,
  TextHighlightAnnotation,
} from '@readcloud/data';
import C from 'classnames';
import { getTextRectsForRange } from '../../../utils/rangeUtils';

import {
  annotationsActions,
  annotationsSelectors,
  useAppDispatch,
} from '@readcloud/state';
import { useSelector } from 'react-redux';
import { TextSelectionContext } from '../../../context/TextSelection';
import { ConnectedAnnotationPopover } from '../../Modals/ConnectedAnnotationPopover';

function forEachReverse<T>(fn: (item: T) => any, array: T[]) {
  for (let i = array.length - 1; i >= 0; i--) {
    fn(array[i]);
  }
}

interface Rect {
  top: number;
  left: number;
  height: number;
  width: number;
}
const pointIsInRect = ({ top, left, height, width }: Rect, [x, y]: number[]) =>
  x >= left && x <= left + width && y >= top && y <= top + height;

type Props = {
  pageNumber: number;
  scale: number;
  visible: boolean;
  annotationPopoverContainer: HTMLElement;
  height: React.CSSProperties['height'];
  width: React.CSSProperties['width'];
  className?: string;
  style?: React.CSSProperties;

  /** The current selected annotation id. Use this prop to show an annotation popover for that annotation id */
  selectedAnnotationId?: string;
  annotationPopoverZIndex?: number;
  zIndex?: number;
  annotations: Annotation[];
  /** Update this prop to cause a redraw */
  textLayerId?: string;

  /** The textLayer which will be used as a range root, to deserialize annotations. Serialized annotations are serialized relative to a root element. This should be that same element that was used to serialize. */
  rootContainer: HTMLElement;
};

export const PDFHighlightsRenderer: React.FC<Props> = (props: Props) => {
  const [extraChildren, setExtraChildren] = React.useState<React.ReactNode[]>(
    []
  );
  const [annotations, setAnnotations] = React.useState<
    PDFTextHighlightAnnotation[]
  >([]);
  const [annotationRects, setAnnotationRects] = React.useState<{
    [k: string]: DOMRect[];
  }>({});
  const [selectedAnnotation, setSelectedAnnotation] = React.useState<
    TextHighlightAnnotation | PDFTextHighlightAnnotation
  >();

  const globalSelectedAnnotation = useSelector(
    annotationsSelectors.getSelectedAnnotation
  );

  const { customTextSelection } = React.useContext(TextSelectionContext);

  const dispatch = useAppDispatch();

  React.useEffect(() => {
    const tempAnnotations: PDFTextHighlightAnnotation[] = [];
    if (
      props.annotations &&
      annotations &&
      JSON.stringify(props.annotations) === JSON.stringify(annotations)
    )
      return;

    props.annotations.forEach((anno) => {
      if (anno.type === 'Text' && anno?.data?.rects?.length) {
        tempAnnotations.push(anno);
      }
    });

    setAnnotations(tempAnnotations);
  }, [props.annotations]);

  const selectAnnotation = (
    annotation: PDFTextHighlightAnnotation | undefined
  ) => {
    dispatch(annotationsActions.setSelectedAnnotation(annotation?.id || ''));
  };

  const tapHandler = (e: MouseEvent) => {
    if (
      e.target instanceof HTMLElement &&
      !e.target.closest('#annotation-popover')
    ) {
      selectAnnotation(undefined);
    }
    const boundingRect = props.rootContainer.getBoundingClientRect();
    const x = e.pageX - boundingRect.left;
    const y = e.pageY - boundingRect.top;
    const point = [x, y];

    let found = false;
    forEachReverse((annotation) => {
      if (found) return;
      const rects = annotationRects[annotation.id] || [];

      if (rects.find((rect) => pointIsInRect(rect, point))) {
        // Prevent default is required here because of a material-ui issue on mobile https://github.com/mui-org/material-ui/issues/3335. It is also required that this handler is from a "click" event
        e.preventDefault();
        selectAnnotation(annotation);
        found = true;
        return;
      }
    }, annotations || []);
  };

  const redraw = (cb?: () => void) => {
    const filteredParsedAnnotations = props?.annotations?.filter(
      (annotation: Annotation) =>
        annotation?.type === 'Text' && annotation?.data?.rects
    ) as PDFTextHighlightAnnotation[];

    const annotationRects = filteredParsedAnnotations?.reduce(
      (acc: { [k: string]: DOMRect[] }, annotation) => {
        const adjustRect = ({
          top,
          left,
          right,
          bottom,
          width,
          height,
        }: any): DOMRect => {
          const rect = new DOMRect(
            left * props.scale,
            top * props.scale,
            width * props.scale,
            height * props.scale
          );
          return rect;
        };

        const rects =
          annotation.data?.rects?.map((rect) => adjustRect(rect)) || [];
        return {
          ...acc,
          [annotation.id]: rects,
        };
      },
      {}
    );

    setAnnotationRects(annotationRects);

    setExtraChildren(() =>
      filteredParsedAnnotations?.map((annotation) => (
        <React.Fragment key={annotation.id}>
          {annotationRects[annotation.id].map(
            ({ top, left, height, width }, i) => (
              <div
                key={`${i}`}
                className="textSelectAnnoFix"
                style={{
                  opacity: 0.5,
                  position: 'absolute',
                  top,
                  left,
                  height,
                  width,
                  backgroundColor: annotation.color,
                }}
              />
            )
          )}
        </React.Fragment>
      ))
    );
    cb && cb();
  };

  const setupContentLayerListener = () => {
    // It is very important that this remains as a "click" event, and that preventDefault is called within the handler before the Popover is shown - https://github.com/mui-org/material-ui/issues/3335
  };

  // this will change the selectedComponent state of this component if the globally selected Anno is not from this component.
  // This is a little bit of a hack, I would like to re-organise this component so that  an internal component state isn't necessary.
  React.useEffect(() => {
    if (globalSelectedAnnotation?.id) {
      if (
        (globalSelectedAnnotation as PDFTextHighlightAnnotation)?.location
          ?.pageNumber === props.pageNumber &&
        globalSelectedAnnotation.type === 'Text' &&
        globalSelectedAnnotation.id !== selectedAnnotation?.id
      ) {
        setSelectedAnnotation(
          globalSelectedAnnotation as PDFTextHighlightAnnotation
        );
      } else {
        setSelectedAnnotation(undefined);
      }
    } else {
      setSelectedAnnotation(undefined);
    }
  }, [globalSelectedAnnotation?.id]);

  React.useEffect(() => {
    if (props.annotationPopoverContainer) {
      props.annotationPopoverContainer?.addEventListener('click', tapHandler);
    }
    return () => {
      //document.removeEventListener('click', () => selectAnnotation(undefined));
      props.annotationPopoverContainer?.removeEventListener(
        'click',
        tapHandler
      );
    };
  }, [props.annotationPopoverContainer, annotationRects]);

  React.useEffect(() => {
    redraw();
  }, [props.scale, annotations]);

  return (
    <>
      <div
        className={C('rc-absolute', props.className)}
        style={{
          ...props.style,
          overflow: 'hidden',
          height: props.height,
          width: props.width,
          top: 0,
          left: 0,
        }}
      >
        <div
          style={{
            zIndex: 1,
            height: props.height,
            width: props.width,
          }}
          className={C(
            'safariScrollFix',
            customTextSelection ? 'disableTextSelectIOS' : '',
            'position-relative'
          )}
        >
          {extraChildren}
        </div>
      </div>
      <ConnectedAnnotationPopover
        selectedAnnotation={selectedAnnotation as PDFTextHighlightAnnotation}
        container={props.annotationPopoverContainer}
        deselectAnnotation={() => selectAnnotation(undefined)}
      />
    </>
  );
};
